import { initializeModalAccessibilityOverlay } from './modalAccessibility';
import { modalMarkups } from './markup/modalMarkups';
import { playerMarkup } from './markup/playerMarkup';
import type { Root } from './prototypeTypes';

/**
 * Business dialogs stay out of the server-rendered Topic/Chapter document.
 * They use the exact SPA templates and are mounted once, on first use, into
 * the SSR overlay portal. Credential dialogs remain owned by their separate
 * lifecycle because password fields have stricter mounting rules.
 */
export const BUSINESS_OVERLAY_IDS = [
	'paywallModal',
	'submitIdeaModal',
	'aiCreateModal',
	'aiChapterLowRangeModal',
	'aiSuccessModal',
	'ideaSuccessModal',
	'verifyEmailOverlay',
	'setPasswordConfirmModal',
	'cancelAutoRenewModal',
	'upgradeResumeModal',
	'upgradeConfirmModal',
	'legalDocumentModal',
	'mermaidFullscreenModal',
	'communityIdeaDetailModal',
	'aiSubmissionDetailModal',
] as const;

export type BusinessOverlayId = (typeof BUSINESS_OVERLAY_IDS)[number];

const businessOverlayIdSet = new Set<string>(BUSINESS_OVERLAY_IDS);
const businessOverlayMarkup = modalMarkups.join('');

export function ensureBusinessOverlayMounted(root: Root, id: string): HTMLElement | null {
	const document = ownerDocument(root);
	const existing = document?.getElementById(id);
	if (isHtmlElement(existing)) return existing;
	if (!document || !businessOverlayIdSet.has(id)) return null;

	const portal = ensureOverlayPortal(document);
	const template = document.createElement('template');
	template.innerHTML = businessOverlayMarkup;
	const overlay = template.content.querySelector<HTMLElement>(`#${id}`);
	if (!overlay) return null;

	// Only this requested top-level overlay leaves the template. Never append
	// the entire static set or re-run accessibility initialization globally.
	overlay.hidden = true;
	overlay.classList.remove('active');
	overlay.dataset.businessOverlayMount = 'lazy';
	initializeModalAccessibilityOverlay(overlay);
	portal.appendChild(overlay);
	return overlay;
}

/** Mounts the two client-only media surfaces before SSR controller wiring. */
export function mountSsrMediaSurfaces(root: Root): void {
	const document = ownerDocument(root);
	if (!document) return;
	const portal = ensureOverlayPortal(document);
	const template = document.createElement('template');
	template.innerHTML = playerMarkup;

	for (const id of ['webPlayer', 'videoFullscreen']) {
		if (document.getElementById(id)) continue;
		const surface = template.content.querySelector<HTMLElement>(`#${id}`);
		if (surface) portal.parentNode?.insertBefore(surface, portal);
	}
}

function ensureOverlayPortal(document: Document): HTMLElement {
	const existing = document.getElementById('app-overlay-portal');
	if (isHtmlElement(existing)) return existing;
	const portal = document.createElement('div');
	portal.id = 'app-overlay-portal';
	portal.dataset.overlayPortal = '';
	document.body.appendChild(portal);
	return portal;
}

function ownerDocument(root: Root): Document | null {
	const candidate = (root as unknown as { ownerDocument?: unknown }).ownerDocument ?? root;
	return isDocumentLike(candidate) ? candidate : null;
}

function isDocumentLike(value: unknown): value is Document {
	if (!value || typeof value !== 'object') return false;
	const candidate = value as Partial<Document>;
	return typeof candidate.getElementById === 'function'
		&& typeof candidate.createElement === 'function'
		&& Boolean(candidate.body);
}

function isHtmlElement(value: unknown): value is HTMLElement {
	if (!value || typeof value !== 'object') return false;
	const candidate = value as Partial<HTMLElement>;
	return typeof candidate.querySelector === 'function'
		&& typeof candidate.setAttribute === 'function'
		&& typeof candidate.appendChild === 'function';
}
